/**
 * Call capture on the calling screen, for every app: live transcription (polled while it runs), recording, and
 * waiting for what's saved after a call ends. Each hook works on the app's call records through its transport.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { isFinishedCallStatus, MAX_TRANSCRIPT_PAGE, } from '@common/voice';
const POLL_MS = 1000;
/** Twilio sends a call's last lines within seconds of transcription stopping; ask this many more times. */
const POLLS_AFTER_STOP = 15;
/** The summary is written about 20 seconds after the call ends; Twilio saves a recording within a minute or so. */
const SUMMARY_POLL_MS = 3000;
const MAX_SUMMARY_POLLS = 40;
/** The server hears a call was answered from Twilio, a moment after the browser: starting waits that long, at most. */
const ANSWER_WAIT_POLLS = 8;
const ANSWER_WAIT_MS = 1000;
function stillSaving(call, expectSummary, stillUpdating) {
    const summaryDone = call !== null && call.summary !== null && call.summary.status !== 'pending';
    const recording = call === null ? null : call.recording;
    const recordingSaving = recording !== null &&
        (recording.status === 'recording' || recording.status === 'paused' || recording.status === 'processing');
    return (expectSummary && !summaryDone) || recordingSaving || (call !== null && stillUpdating(call));
}
/**
 * Asks for a call that ended until its summary (when transcribed), its recording and what the app still updates
 * (`stillUpdating`) are saved, so the wrap-up shows them.
 */
export function useEndedCallUpdates(voice, call, stillUpdating) {
    const { recentCalls, rememberCall, transport } = voice;
    const { callId, ended, expectSummary } = call;
    const record = recentCalls.find((item) => item.id === callId) ?? null;
    const waiting = ended && stillSaving(record, expectSummary, stillUpdating);
    useEffect(() => {
        if (callId === null || !waiting)
            return undefined;
        let cancelled = false;
        let timer = 0;
        let polls = 0;
        const tick = async () => {
            polls += 1;
            try {
                const latest = await transport.getCall(callId);
                if (cancelled)
                    return;
                rememberCall(latest);
                if (!stillSaving(latest, expectSummary, stillUpdating))
                    return;
            }
            catch {
                // The transport already showed why; the notes don't need the summary.
                return;
            }
            if (polls < MAX_SUMMARY_POLLS)
                timer = window.setTimeout(() => void tick(), SUMMARY_POLL_MS);
        };
        timer = window.setTimeout(() => void tick(), SUMMARY_POLL_MS);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [transport, callId, waiting, expectSummary, rememberCall, stillUpdating]);
}
/** A call's transcript, polled while it's being transcribed (and shortly after, for the last lines). */
export function useCallTranscript(voice, callId) {
    const { recentCalls, rememberCall, transport } = voice;
    const [transcription, setTranscription] = useState(() => recentCalls.find((call) => call.id === callId)?.transcription ?? null);
    const [lines, setLines] = useState([]);
    const [live, setLive] = useState([]);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const cursorRef = useRef('');
    const started = transcription !== null;
    const active = transcription !== null && transcription.active;
    useEffect(() => {
        if (callId === null || !started || failed)
            return undefined;
        let cancelled = false;
        let timer = 0;
        let idle = 0;
        const tick = async () => {
            let res;
            try {
                res = await transport.transcript(callId, cursorRef.current);
            }
            catch {
                // The transport already showed why; polling again every second would repeat it.
                if (!cancelled)
                    setFailed(true);
                return;
            }
            if (cancelled)
                return;
            const page = res.lines;
            if (page.length > 0) {
                cursorRef.current = page[page.length - 1].key;
                setLines((current) => [...current, ...page]);
            }
            setLive(res.live);
            setTranscription(res.transcription);
            idle = res.transcription !== null && res.transcription.active ? 0 : idle + 1;
            if (page.length === MAX_TRANSCRIPT_PAGE) {
                void tick();
            }
            else if (idle <= POLLS_AFTER_STOP) {
                timer = window.setTimeout(() => void tick(), POLL_MS);
            }
        };
        void tick();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [transport, callId, started, active, failed, attempt]);
    const retry = useCallback(() => {
        setFailed(false);
        setAttempt((value) => value + 1);
    }, []);
    const setActive = useCallback(async (on, personAgreed) => {
        if (callId === null)
            return;
        setBusy(true);
        try {
            const call = await transport.setTranscription(callId, { active: on, person_agreed: personAgreed });
            rememberCall(call);
            setFailed(false);
            setTranscription(call.transcription);
        }
        catch {
            // The transport already showed why.
        }
        finally {
            setBusy(false);
        }
    }, [transport, callId, rememberCall]);
    const startWhenAnswered = useCallback(async (personAgreed) => {
        if (callId === null)
            return;
        let answered = false;
        setBusy(true);
        try {
            for (let poll = 1; poll <= ANSWER_WAIT_POLLS && !answered; poll += 1) {
                const call = await transport.getCall(callId);
                if (isFinishedCallStatus(call.status))
                    break;
                answered = call.status === 'in_progress';
                if (!answered && poll < ANSWER_WAIT_POLLS) {
                    await new Promise((resolve) => { window.setTimeout(resolve, ANSWER_WAIT_MS); });
                }
            }
        }
        catch {
            // The transport already showed why.
        }
        setBusy(false);
        if (answered)
            await setActive(true, personAgreed);
    }, [transport, callId, setActive]);
    return { transcription, lines, live, failed, retry, busy, setActive, startWhenAnswered };
}
/** The call's recording as the caller starts, pauses and resumes it; Twilio saves it once the call ends. */
export function useCallRecording(voice, callId) {
    const { recentCalls, rememberCall, transport } = voice;
    const [busy, setBusy] = useState(false);
    const recording = recentCalls.find((call) => call.id === callId)?.recording ?? null;
    const act = useCallback(async (action, personAgreed) => {
        if (callId === null)
            return;
        setBusy(true);
        try {
            rememberCall(await transport.setRecording(callId, { action, person_agreed: personAgreed }));
        }
        catch {
            // The transport already showed why.
        }
        finally {
            setBusy(false);
        }
    }, [transport, callId, rememberCall]);
    return { recording, busy, act };
}
// Calls already offered automatic transcription, so minimizing and reopening the screen doesn't offer it again.
const autoTranscribeOffered = new Set();
/**
 * What the calling screen offers to capture a call: transcription and recording as the org's policy allows them,
 * asking the caller to confirm consent when the policy says so, and transcribing every call when the caller chose to.
 * After the call it waits for what's still being saved (`stillUpdating` adds the app's own work).
 */
export function useCallCapture(voice, call, stillUpdating) {
    const { phone } = voice;
    const { current, live } = call;
    const transcript = useCallTranscript(voice, current.callId);
    const recorder = useCallRecording(voice, current.callId);
    useEndedCallUpdates(voice, {
        callId: current.callId, ended: live === null, expectSummary: transcript.lines.length > 0,
    }, stillUpdating);
    const [asking, setAsking] = useState(null);
    const policy = phone.status === 'ready' ? phone.transcription : null;
    const transcribing = transcript.transcription !== null && transcript.transcription.active;
    const transcribe = live === null || policy === null || !policy.allowed ? null : {
        active: transcribing,
        busy: transcript.busy,
        onToggle: () => {
            if (transcribing)
                void transcript.setActive(false, false);
            else if (policy.confirm_consent)
                setAsking('transcribed');
            else
                void transcript.setActive(true, false);
        },
    };
    // A caller who transcribes all their calls is asked (or the call starts transcribed) as soon as it connects.
    const autoTranscribe = phone.status === 'ready' && phone.preferences.auto_transcribe;
    const answered = live !== null && live.stage === 'open' && live.answeredAt !== null;
    const untranscribed = transcript.transcription === null;
    const { startWhenAnswered } = transcript;
    useEffect(() => {
        const id = current.callId;
        if (!autoTranscribe || !answered || !untranscribed || id === null || policy === null || !policy.allowed)
            return;
        if (autoTranscribeOffered.has(id))
            return;
        autoTranscribeOffered.add(id);
        if (policy.confirm_consent)
            setAsking('transcribed');
        else
            void startWhenAnswered(false);
    }, [autoTranscribe, answered, untranscribed, current.callId, policy, startWhenAnswered]);
    const recordingPolicy = phone.status === 'ready' ? phone.recording : null;
    const recordingStatus = recorder.recording === null ? null : recorder.recording.status;
    const record = live === null || recordingPolicy === null || !recordingPolicy.allowed ? null : {
        status: recordingStatus,
        busy: recorder.busy,
        onToggle: () => {
            if (recordingStatus === 'recording')
                void recorder.act('pause', false);
            else if (recordingStatus === 'paused')
                void recorder.act('resume', false);
            else if (recordingPolicy.confirm_consent)
                setAsking('recorded');
            else
                void recorder.act('start', false);
        },
    };
    const consent = {
        open: asking !== null,
        capture: asking ?? 'transcribed',
        onCancel: () => setAsking(null),
        onConfirm: () => {
            setAsking(null);
            if (asking === 'recorded')
                void recorder.act('start', true);
            else
                void transcript.startWhenAnswered(true);
        },
    };
    return { transcript, recorder, transcribe, record, consent };
}
